import { setExtra, captureMessage } from '@sentry/nextjs';
import { getValidToken } from '@utilities/getValidToken';
import { NextRouter } from 'next/router';

import { fireToast } from '@hooks/fireToast.js';

const PAYMENT_SERVICE_API = process.env.PAYMENTS_API_URL;
const PAYMENT_LINK_API = process.env.PAYMENT_LINK_API;

interface makeRequestAPIsProps {
  body?: object | string;
  url: string;
  method: 'GET' | 'POST' | 'DELETE'
}

const makeRequestAPIs = async ({ body, url, method }: makeRequestAPIsProps) => {
  // @ts-ignore we add this to window.
  const token = window.webViewAmplifyAuthToken || await getValidToken();
  let config = {
    method,
    // eslint-disable-next-line no-undef
    mode: 'cors' as RequestMode,
    keepalive: true,
    // @ts-ignore
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token
    }
  };

  if (body) {
    // @ts-ignore
    config.body = JSON.stringify(body);
  }

  return fetch(url, config)
    .then(async (res) => {
      if (res.status !== 200) {
        return {error: await res.text()};
      }
      return await res.json();
    })
    .catch(err => {
      setExtra('info', {
        error: `${JSON.stringify(err)}`,
        url
      });
      captureMessage('Failed to call payment link API', 'fatal');
      return {
        failed: true,
        error: err
      };
    });
};

const failedAPIReq = ({ error, url, apiName }:
  { error: any, url: string, apiName: string }) => {
  setExtra('info', {
    error: `${JSON.stringify(error)}`,
    url
  });
  captureMessage(`Failed to call ${apiName} API`, 'fatal');

  return false;
};

export const initiateStripeCheckout = async ({ setLoading, setResError, interval, router, promotionCode }:
  { setLoading: Function, setResError: Function, interval: string, router: any, promotionCode?: string }) => {
  const url = `${PAYMENT_SERVICE_API}/stripe/create-checkout-session`;
  setLoading(true);

  const res = await makeRequestAPIs({ url, method: 'POST', body: { interval, promotionCode } })
    .then(async (res) => {
      if (res.error) return { failed: true, error: res.error };
      return await router.push(res.checkoutSessionUrl);
    });

  if (res.failed) {
    setLoading(false);
    setResError(res.error);
    return failedAPIReq({ error: res.error, url, apiName: 'stripe' });
  }
};

export const createManagementLink = async ({ setLoading, setResError, router }:
  { setLoading: Function, setResError: Function, interval: string, router: any }) => {
  const url = `${PAYMENT_SERVICE_API}/stripe/create-management-link`;

  setLoading(true);

  const res = await makeRequestAPIs({ url, method: 'POST' })
    .then((res) => {
      if (res.error) return { failed: true, error: res.error };
      return res;
    });

  if (res.failed) {
    setResError(res.error);
    return failedAPIReq({ error: res.error, url, apiName: 'stripe' });
  }

  await router.push(res.managementUrl);
};

interface createPaymentLinkAccountProps { 
  router?: NextRouter;
  type: 'statusCheck' | 'click';
  setLoading?: Function;
  setError?: Function;
  bootUp?: boolean;
  returnUrl?: string;
}

export const createPaymentLinkAccount = async ({ router, type, setLoading, setError = () => {}, bootUp = false, returnUrl }: createPaymentLinkAccountProps) => {
  const urlPath = type === 'click' ? 'create-payment-link-account' : 'check-for-payment-link-account';
  const method = type === 'click' ? 'POST' : 'GET';
  const url = `${PAYMENT_LINK_API}/${urlPath}`;
  const optionalBody = returnUrl ? { body: { returnUrl } } : {};

  // Bail out if an early set isn't required.
  if (bootUp) {
    return false;
  }

  const res = await makeRequestAPIs({ url, method, ...optionalBody })
    .then(async (res) => {
      if (res.error) return { failed: true, error: res.error };
      if (router && type === 'click') {
        await router.push(res.url);
      }
      if (type === 'click' && setLoading) setLoading(false);
      return res;
    });

  // @ts-ignore
  if (res.failed && (type === 'click' || window.webViewNoAuth === true)) {
    // Fire toast as this was triggered from a user interaction.
    fireToast({
      type: 'error',
      message: 'There seems to be an issue with the payment link service ' +
        '- The Yepic team has been notified. Sorry for the inconvenience',
      id: 'paymentLinkError',
      theme: null
    });

    if (res.failed) {
      setError(res.error);

      return failedAPIReq({ error: res.error, url, apiName: 'payment link' });
    }
  }

  setError(false);

  return res;

};

import { lineItem } from '@components/molecules/lineItems';
export const createPaymentLinkRequest = async ({ lineItems, label, jobId }
  : { lineItems: lineItem[], label: string, jobId: string | null }) => {
  const url = `${PAYMENT_LINK_API}/create-payment-link`;

  const res = await makeRequestAPIs({
    url,
    method: 'POST',
    body: {
      lineItems,
      jobId,
      label
    }
  })
    .then((res) => {
      if (res.error) return { failed: true, error: res.error };
      const { paymentLink: { url } } = res;
      return url;
    });

  if (res.failed) {
    return failedAPIReq({ error: res.error, url, apiName: 'payment link' });
  }
  return res;
};


export const listPaymentLinkRequest = async () => {
  const url = `${PAYMENT_LINK_API}/get-payment-links`;

  const res = await makeRequestAPIs({ url, method: 'GET' })
    .then((res) => {
      if (res.error) return { failed: true, error: res.error };
      return res;
    });

  if (res.failed) {
    return failedAPIReq({ error: res.error, url, apiName: 'payment link' });
  }

  return res;
};

export const deletePaymentLinkRequest = async ({ paymentLinkId }: { paymentLinkId: string }) => {
  const url = `${PAYMENT_LINK_API}/delete/${paymentLinkId}`;

  const res = await makeRequestAPIs({ url, method: 'DELETE' })
    .then((res) => {
      if (res.error) return { failed: true, error: res.error };
      return res;
    });

  if (res.failed) {
    return failedAPIReq({ error: res.error, url, apiName: 'payment link' });
  }

  return res;
};